/**
 * <semi360-loader> — the suite's one loading indicator: Sami's mark, working.
 *
 * Every product had its own idea of "loading": a sentence, a grey pulse, a
 * borrowed spinner. Meanwhile the copilot dock already owned a motion for
 * "Sami is working" — the zero glyph turning with its satellite racing — and
 * a reader who has seen the dock knows exactly what that motion means. So
 * loading IS that motion, everywhere, defined once here.
 *
 * Ships as /embed/loader.js, and is also registered by the header bundle —
 * a page that already has the header needs no extra script tag:
 *
 *   <semi360-loader label="Resolving BAT54…"></semi360-loader>
 *
 * Attributes:
 *   label   the sentence under the mark (optional; screen readers get it too)
 *   size    disc diameter in px (default 44)
 *   bare    present = no navy disc, just the turning mark — for tight rows
 */

import { ensureTokens } from "./tokens";

/** This deployment's Hub base URL, inlined at build time by
 *  scripts/build-embed.mjs; last resort only. */
declare const __HUB_BASE__: string;

/** The origin that served this file. Read at script-evaluation time — by the
 *  time an element connects, document.currentScript is null. */
const SCRIPT_ORIGIN: string = (() => {
  try {
    const el = document.currentScript as HTMLScriptElement | null;
    return el && el.src ? new URL(el.src).origin : "";
  } catch {
    return "";
  }
})();

function hubBase(): string {
  if (SCRIPT_ORIGIN) return SCRIPT_ORIGIN;
  const { protocol, hostname, port } = window.location;
  const parts = hostname.split(".");
  if (parts.length > 2) return `${protocol}//hub.${parts.slice(1).join(".")}${port ? `:${port}` : ""}`;
  return (__HUB_BASE__ || "https://hub.dev.semi360.ai").replace(/\/+$/, "");
}

// The Semi360 "zero" from the wordmark — same glyph the dock launches with.
const ZERO_D =
  "M892.898 0C838.622 0 794.627 44.1794 794.627 98.6843C794.627 153.189 838.622 197.353 892.898 197.353C947.175 197.353 991.169 153.174 991.169 98.6843C991.169 44.1948 947.175 0 892.898 0ZM892.898 132.923C874.096 132.923 858.803 117.566 858.803 98.6843C858.803 79.803 874.096 64.4303 892.898 64.4303C911.7 64.4303 926.993 79.803 926.993 98.6843C926.993 117.566 911.7 132.923 892.898 132.923ZM945.382 98.6843C945.382 69.5699 921.89 45.9645 892.898 45.9645C873.56 45.9645 856.657 56.4746 847.555 72.1089C853.087 44.718 877.268 24.0363 906.138 24.0363C939.084 24.0363 965.9 50.9656 965.9 84.0502C965.9 111.056 948.048 133.939 923.576 141.448C936.785 131.877 945.382 116.288 945.382 98.6843ZM892.898 178.887C848.858 178.887 813.016 142.91 813.016 98.6843C813.016 71.8935 826.163 48.1188 846.329 33.5462C834.883 47.1955 827.987 64.815 827.987 84.0502C827.987 127.399 862.971 162.53 906.138 162.53C928.02 162.53 947.788 153.512 961.977 138.955C948.109 162.822 922.335 178.887 892.898 178.887Z";

import { ensureTokens } from "./tokens";
import { detectHubBase } from "./products";

declare const __HUB_BASE__: string;

const STYLE = `
:host{ all:initial; display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:10px; padding:14px 10px;
  font-family:var(--font-ui);
  /* Named, not scattered (unified CSS standard §4a). The suite publishes no
     alpha or tint tokens, so a brand colour at 50%/28%/85% opacity cannot be
     expressed as a token at all — and color-mix(in srgb, var(--brand-teal)
     50%, transparent) is the only token-based way to write it, which a 2022
     browser drops entirely (the whole shadow disappears, silently). These are
     --brand-teal at three alphas; see the report's proposed tint tokens.
     They are declared once, here, so that becomes a three-line change. */
  --s360ld-glow-ring:rgba(36,208,192,.5);
  --s360ld-glow-soft:rgba(36,208,192,.28);
  --s360ld-glow-orb:rgba(36,208,192,.85); }
.disc{position:relative;border-radius:50%;background:var(--brand-ink);display:grid;place-items:center;
  box-shadow:0 0 0 2px var(--s360ld-glow-ring), 0 0 14px var(--s360ld-glow-soft)}
/* the working motion, verbatim from the dock's busy state: the zero turns,
   the satellite races */
.zero{display:grid;place-items:center;animation:s360ldturn 1.7s linear infinite}
.zero svg{display:block;fill:var(--brand-teal)}
.orb{position:absolute;inset:-4px;border-radius:50%;pointer-events:none;animation:s360ldspin .9s linear infinite}
.orb::before{content:"";position:absolute;top:-2.5px;left:50%;width:6px;height:6px;margin-left:-3px;
  border-radius:50%;background:var(--brand-teal);box-shadow:0 0 8px 2px var(--s360ld-glow-orb)}
:host([bare]) .disc{background:none;box-shadow:none}
:host([bare]) .orb{display:none}
.lbl{font-size:12.5px;line-height:1.5;color:var(--fg-3);text-align:center;max-width:46ch}
@keyframes s360ldturn{to{transform:rotate(360deg)}}
@keyframes s360ldspin{to{transform:rotate(360deg)}}
@media(prefers-reduced-motion:reduce){.zero,.orb{animation:none}}
`;

class Semi360Loader extends HTMLElement {
  private root: ShadowRoot;
  static get observedAttributes() {
    return ["label", "size", "bare"];
  }
  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }
  connectedCallback() {
    // The loader now reads tokens rather than restating the palette, and a
    // product may load /embed/loader.js on a page with no header and no
    // tokens.css. Custom properties inherit into Shadow DOM — including past
    // this element's own `:host{all:initial}`, which does not reset them —
    // but only if the document actually has them.
    ensureTokens(
      detectHubBase("/embed/loader.js", (__HUB_BASE__ || "https://hub.dev.semi360.ai").replace(/\/+$/, "")),
    );
    this.render();
  }
  attributeChangedCallback() {
    if (this.root.firstChild) this.render();
  }
  private render() {
    const size = Math.max(16, Math.min(96, parseInt(this.getAttribute("size") || "44", 10) || 44));
    const glyph = Math.round(size * 0.52);
    const label = this.getAttribute("label") || "";
    // role/aria-label live INSIDE the shadow root: attributes written onto
    // the host element itself differ from the server-rendered markup and
    // trip React's hydration warning on every Next page.
    this.root.innerHTML = `<style>${STYLE}</style>
      <span class="disc" role="status"${label ? ` aria-label="${label.replace(/"/g, "&quot;").replace(/</g, "&lt;")}"` : ""} style="width:${size}px;height:${size}px">
        <span class="orb"></span>
        <span class="zero"><svg viewBox="792 -3 203 203" width="${glyph}" height="${glyph}" class="z" fill="currentColor"><path d="${ZERO_D}"/></svg></span>
      </span>
      ${label ? `<span class="lbl"></span>` : ""}`;
    if (label) {
      const el = this.root.querySelector(".lbl");
      if (el) el.textContent = label; // textContent: a label may carry an MPN from user input
    }
  }
}

if (!customElements.get("semi360-loader")) {
  customElements.define("semi360-loader", Semi360Loader);
}

export {};
